import React, { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { api } from "../utils/api";
import { PageHeader, Card } from "../components/ui/Card";
import { StatCard } from "../components/ui/StatCard";
import { Badge } from "../components/ui/Badge";
import { TableCard, td, trStyle } from "../components/ui/Table";
import { colors } from "../components/ui/tokens";
import { CheckIcon, XIcon, StarIcon } from "../components/ui/icons";

const IconUsers = ({ color }: { color: string }) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const IconPubs = ({ color }: { color: string }) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>;
const IconAlert = ({ color }: { color: string }) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
const IconMoney = ({ color }: { color: string }) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
const IconPulse = ({ color }: { color: string }) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>;
const IconUserX = ({ color }: { color: string }) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="18" y1="8" x2="23" y2="13"/><line x1="23" y1="8" x2="18" y2="13"/></svg>;
const IconTrash = ({ color }: { color: string }) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>;
const IconDownload = ({ color }: { color: string }) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>;

// Réservé au compte master (voir Users.tsx#isMasterAdmin, même lecture directe de localStorage —
// pas de contexte partagé dans oltodo-admin) : un "Bienvenue David" personnel plutôt que le
// générique "Bienvenue sur le panel OLTODO" vu par l'équipe. Prénom seul (premier mot du nom).
const masterFirstName = (): string | null => {
  try {
    const u = JSON.parse(localStorage.getItem("admin_user") || "null");
    return u?.is_master_admin && u?.name ? String(u.name).split(" ")[0] : null;
  } catch { return null; }
};

export default function Dashboard() {
  const [users, setUsers] = useState<any[]>([]);
  const [publications, setPublications] = useState<any[]>([]);
  const [reports, setReports] = useState<any[]>([]);
  const [revenue, setRevenue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState<{ api: boolean; database: boolean }>({ api: false, database: false });
  const [extraStats, setExtraStats] = useState({
    online_count: 0, deleted_count: 0, deactivated_count: 0,
    apple_downloads: null as number | null, google_downloads: null as number | null,
  });

  useEffect(() => {
    api.get("/users")
      .then(data => { if (data && Array.isArray(data.users)) setUsers(data.users); })
      .catch(() => {})
      .finally(() => setLoading(false));

    api.get("/publications").then(data => { if (data?.publications) setPublications(data.publications); }).catch(() => {});
    api.get("/ratings").then(data => { if (data?.reports) setReports(data.reports); }).catch(() => {});
    api.get("/payments").then(data => { if (data?.stats) setRevenue(Number(data.stats.total_revenue) || 0); }).catch(() => {});
    api.get("/users/admin/stats").then(data => { if (data && !data.error) setExtraStats(data); }).catch(() => {});

    fetch(`${process.env.REACT_APP_API_URL || "https://api.oltodo.com/api"}/health`)
      .then(r => r.ok ? r.json() : null)
      .then(data => setHealth({ api: !!data, database: data?.database === "connected" }))
      .catch(() => setHealth({ api: false, database: false }));
  }, []);

  const today = new Date().toDateString();
  const newToday = users.filter(u => new Date(u.created_at).toDateString() === today).length;
  const activePubs = publications.filter(p => p.status === "active").length;
  const pendingReports = reports.filter(r => !r.is_resolved).length;

  const recentUsers = [...users]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);

  const weekDays = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
  const chartData = Array.from({ length: 7 }).map((_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    const count = users.filter(u => new Date(u.created_at).toDateString() === d.toDateString()).length;
    return { name: weekDays[d.getDay()], users: count };
  });

  return (
    <div style={{ padding: 32 }}>
      <PageHeader title="Dashboard" subtitle={masterFirstName() ? `Bienvenue ${masterFirstName()}` : "Bienvenue sur le panel OLTODO"} />

      <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <StatCard label="Utilisateurs" value={loading ? "…" : users.length} tone="accent" icon={<IconUsers color={colors.accent} />} />
        <StatCard label="Publications actives" value={activePubs} tone="success" icon={<IconPubs color={colors.success} />} />
        <StatCard label="Signalements en attente" value={pendingReports} tone="danger" icon={<IconAlert color={colors.danger} />} />
        <StatCard label="Revenus totaux" value={`${revenue.toFixed(2)} €`} tone="warning" icon={<IconMoney color={colors.warning} />} />
      </div>

      <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <StatCard label="Profils connectés" value={extraStats.online_count} tone="success" icon={<IconPulse color={colors.success} />} />
        <StatCard label="Comptes désactivés" value={extraStats.deactivated_count} tone="warning" icon={<IconUserX color={colors.warning} />} />
        <StatCard label="Comptes supprimés" value={extraStats.deleted_count} tone="danger" icon={<IconTrash color={colors.danger} />} />
        <StatCard
          label="Téléchargements iOS"
          value={extraStats.apple_downloads == null ? <span style={{ fontSize: 13, fontWeight: 600, color: colors.textFaint }}>Non connecté</span> : extraStats.apple_downloads.toLocaleString()}
          tone="accent" icon={<IconDownload color={colors.accent} />}
        />
        <StatCard
          label="Téléchargements Android"
          value={extraStats.google_downloads == null ? <span style={{ fontSize: 13, fontWeight: 600, color: colors.textFaint }}>Non connecté</span> : extraStats.google_downloads.toLocaleString()}
          tone="accent" icon={<IconDownload color={colors.accent} />}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, marginBottom: 20 }}>
        <Card>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 20, color: colors.text }}>Croissance cette semaine</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: colors.textMuted }} axisLine={{ stroke: colors.border }} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: colors.textMuted }} allowDecimals={false} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 10, border: `1px solid ${colors.border}`, fontSize: 13 }} />
              <Line type="monotone" dataKey="users" stroke={colors.accent} strokeWidth={2.5} dot={false} name="Nouveaux utilisateurs" />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: colors.text }}>Statut système</div>
          {[{ label: "API", ok: health.api }, { label: "Base de données", ok: health.database }].map(item => (
            <div key={item.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: `1px solid ${colors.border}` }}>
              <span style={{ fontSize: 13.5, color: colors.textMuted }}>{item.label}</span>
              <Badge tone={item.ok ? "success" : "danger"} dot>{item.ok ? "En ligne" : "Indisponible"}</Badge>
            </div>
          ))}
        </Card>
      </div>

      <div style={{ marginBottom: 12, fontSize: 15, fontWeight: 700, color: colors.text }}>Derniers inscrits</div>
      <TableCard headers={["Nom", "Email", "Score", "Vérifié", "Date"]}>
        {recentUsers.map((user, i) => (
          <tr key={i} style={trStyle}>
            <td style={{ ...td, fontWeight: 600 }}>{user.name}</td>
            <td style={{ ...td, color: colors.textMuted }}>{user.email}</td>
            <td style={td}><span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: colors.success, fontWeight: 700 }}><StarIcon size={13} color={colors.success} /> {user.score}/10</span></td>
            <td style={td}>
              <Badge tone={user.is_verified ? "success" : "neutral"}>
                {user.is_verified ? <CheckIcon size={11} /> : <XIcon size={11} />} {user.is_verified ? "Vérifié" : "Non vérifié"}
              </Badge>
            </td>
            <td style={{ ...td, color: colors.textFaint, fontSize: 12.5 }}>{new Date(user.created_at).toLocaleDateString("fr-FR")}</td>
          </tr>
        ))}
      </TableCard>
    </div>
  );
}
